import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

let apiPromise = null;
function loadYT() {
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (apiPromise) return apiPromise;
  apiPromise = new Promise((resolve) => {
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    document.body.appendChild(tag);
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (prev) prev();
      resolve(window.YT);
    };
  });
  return apiPromise;
}

const YouTubePlayer = forwardRef(({ videoId, onAction }, ref) => {
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const suppressUntil = useRef(0);
  const lastTime = useRef(0);
  const lastTs = useRef(Date.now());
  const readyRef = useRef(false);
  const pollRef = useRef(null);
  const pendingRemote = useRef(null);
  const destroyTimer = useRef(null);

  const isSuppressed = () => Date.now() < suppressUntil.current;
  const suppress = (ms = 900) => { suppressUntil.current = Date.now() + ms; };

  const doRemote = ({ action, currentTime }) => {
    const p = playerRef.current;
    if (!p || !readyRef.current) { pendingRemote.current = { action, currentTime }; return; }
    suppress();
    try {
      if (action === "play") {
        if (typeof currentTime === "number") p.seekTo(currentTime, true);
        p.playVideo();
      } else if (action === "pause") {
        if (typeof currentTime === "number") p.seekTo(currentTime, true);
        p.pauseVideo();
      } else if (action === "seek") {
        p.seekTo(currentTime, true);
      }
      lastTime.current = currentTime || 0;
      lastTs.current = Date.now();
    } catch (e) { /* noop */ }
  };

  useImperativeHandle(ref, () => ({
    applyRemote(payload) { doRemote(payload); },
    getTime() {
      try { return playerRef.current?.getCurrentTime?.() || 0; } catch { return 0; }
    },
    snapshot() {
      try {
        const p = playerRef.current;
        return { currentTime: p?.getCurrentTime?.() || 0, isPlaying: p?.getPlayerState?.() === 1 };
      } catch { return { currentTime: 0, isPlaying: false }; }
    },
    getState() {
      try { return playerRef.current?.getPlayerState?.(); } catch { return undefined; }
    },
  }));

  const startPoll = () => {
    if (pollRef.current) return;
    pollRef.current = setInterval(() => {
      const p = playerRef.current;
      if (!p || !readyRef.current || isSuppressed()) return;
      try {
        const state = p.getPlayerState();
        const t = p.getCurrentTime();
        if (state === 1) {
          const elapsed = (Date.now() - lastTs.current) / 1000;
          const expected = lastTime.current + elapsed;
          if (Math.abs(t - expected) > 2.2) {
            onAction && onAction("seek", t);
          }
        }
        lastTime.current = t; lastTs.current = Date.now();
      } catch (e) { /* noop */ }
    }, 1000);
  };

  const scheduleDestroy = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (destroyTimer.current) clearTimeout(destroyTimer.current);
    // Defer the teardown. A StrictMode/dev remount fires the next effect within
    // this window and cancels it, so the player is reused (no rebuild flicker).
    destroyTimer.current = setTimeout(() => {
      try { playerRef.current?.destroy?.(); } catch (e) { /* ignore */ }
      playerRef.current = null;
      readyRef.current = false;
      destroyTimer.current = null;
    }, 400);
  };

  useEffect(() => {
    let cancelled = false;
    // Cancel any pending destroy from a StrictMode/dev double-invoke so we REUSE
    // the existing player instead of tearing it down and rebuilding (the "glitch").
    if (destroyTimer.current) { clearTimeout(destroyTimer.current); destroyTimer.current = null; }
    if (playerRef.current) {
      // player already exists (StrictMode remount) — keep it, just resume polling
      startPoll();
      return () => { scheduleDestroy(); };
    }
    loadYT().then((YT) => {
      if (cancelled || !containerRef.current) return;
      playerRef.current = new YT.Player(containerRef.current, {
        videoId: videoId || undefined,
        host: "https://www.youtube-nocookie.com",
        playerVars: { rel: 0, modestbranding: 1, playsinline: 1, autoplay: 1 },
        events: {
          onReady: () => {
            readyRef.current = true;
            if (pendingRemote.current) {
              const pr = pendingRemote.current;
              pendingRemote.current = null;
              doRemote(pr);
            }
          },
          onStateChange: (e) => {
            if (e.data === YT.PlayerState.ENDED) {
              onAction && onAction("ended", 0);
              return;
            }
            if (isSuppressed()) return;
            const t = playerRef.current.getCurrentTime();
            if (e.data === YT.PlayerState.PLAYING) {
              onAction && onAction("play", t);
              lastTime.current = t; lastTs.current = Date.now();
            } else if (e.data === YT.PlayerState.PAUSED) {
              onAction && onAction("pause", t);
              lastTime.current = t; lastTs.current = Date.now();
            }
          },
        },
      });
      startPoll();
    });

    return () => {
      cancelled = true;
      scheduleDestroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const p = playerRef.current;
    if (p && readyRef.current && videoId) {
      suppress();
      try { p.loadVideoById(videoId); } catch (e) { /* ignore */ }
    }
  }, [videoId]);

  return (
    <div className="w-full h-full" data-testid="youtube-player">
      <div ref={containerRef} className="w-full h-full" />
    </div>
  );
});

export default YouTubePlayer;
